import { useLazyQuery, useMutation } from "@apollo/client/react";
import CREATE_TEAM from "@graphql/mutations/CreateTeam";
import { INVITE_USERS_TO_TEAM } from "@graphql/mutations/InviteUsersToTeam";
import GET_EXISTING_USERS from "@graphql/queries/GetExistingUsers";
import {
  useStepNavigation,
  useOnboardingActions,
  useRole,
  useOnboarding,
  useThemeMode,
  useTeam,
  useTemplates,
} from "@hooks";
import mixpanel from "mixpanel-browser";
import moment from "moment";
import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useLocation } from "react-router-dom";

import { OnboardingNavButtons } from "./OnboardingNavButtons";
import { ProgressBar } from "./ProgressBar";
import {
  WelcomeStep,
  RoleStep,
  TemplateChoiceStep,
  TemplateSetupStep,
  CodSetupStep,
  ReferralSourceStep,
  GroupNameStep,
  TeamSetupChoiceStep,
  TeamCreateStep,
  TeamAddUsersStep,
  TeamConfirmStep,
  TeamReceiptStep,
  FinishStep,
} from "./steps";
import { getOnboardingParams, setUrlParams } from "./utils/urlParams";
import starrySky from "../../assets/imgs/onboarding/starry-sky.svg";
import { APPOINTMENT_OBJECTIVE_NORMAL_DEFAULTS } from "../../constants";
import { nonBillableRoles } from "../team/TeamManagement/utils";
import {
  getBillingIncrementText,
  mapExistingUsersResponse,
  validateUsers,
  buildUsersInput,
} from "../team/TeamModal/utils";

export default function Onboarding({ step }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { allowedRoles, role } = useRole();
  const { userTeams } = useTeam();
  const { skipOnboarding } = useOnboarding();

  const urlParams = getOnboardingParams();

  const methods = useForm({
    defaultValues: {
      templateFields: [],
      templateType: "soap",
      objectiveNormalDefaultsFieldArray:
        APPOINTMENT_OBJECTIVE_NORMAL_DEFAULTS.map(
          ({ name, label, type }) => ({
            name,
            value: "",
            label,
            type,
          }),
        ),
    },
  });

  const [selectedRole, setSelectedRole] = useState(
    urlParams.selectedRole || null,
  );
  const [referralSource, setReferralSource] = useState(
    urlParams.referralSource || null,
  );
  const [clinicGroup, setClinicGroup] = useState(
    urlParams.clinicGroup || null,
  );
  const [templateType, setTemplateType] = useState(
    urlParams.templateType || null,
  );
  const [error, setError] = useState("");

  // Capture the team name at mount so that creating a team mid-flow
  // doesn't flip this to truthy and hide the progress bar.
  const [invitedTeamName] = useState(
    () => userTeams?.[0]?.name || null,
  );
  const [templateLoaded, setTemplateLoaded] = useState(false);
  const effectiveSelectedRole =
    selectedRole || (invitedTeamName && role?.id ? role.id : null);

  const [teamName, setTeamName] = useState("");
  const [teamLocation, setTeamLocation] = useState("");
  const [newUserEmails, setNewUserEmails] = useState([]);
  const [newUsersState, setNewUsersState] = useState([]);
  const [existingUsersState, setExistingUsersState] = useState([]);
  const [deactivatedUsersState, setDeactivatedUsersState] = useState(
    [],
  );
  const [receiptData, setReceiptData] = useState({});
  const [newTeam, setNewTeam] = useState({ uuid: "", name: "" });
  const hasBillingSetup = false;
  const orgIsManualBilling = true;

  const {
    currentStep,
    progress,
    navigateToStep,
    syncToStep,
    goBack,
    navigateTeamFlow,
    navigateFromRole,
  } = useStepNavigation(step || "welcome");

  // Reset templateLoaded when we leave the template step (state-during-render)
  if (currentStep !== "template-setup" && templateLoaded) {
    setTemplateLoaded(false);
  }

  // Team mutations/queries
  const [createTeam] = useMutation(CREATE_TEAM);

  const [getExistingUsers] = useLazyQuery(GET_EXISTING_USERS, {
    fetchPolicy: "network-only",
  });

  const [inviteUsersToTeam] = useMutation(INVITE_USERS_TO_TEAM);

  const newUsersEmailArray = newUsersState.filter(
    (user) =>
      !nonBillableRoles.includes(user.roleId) &&
      user.accountType == "unlimited",
  );
  const existingUsersEmailArray = existingUsersState.filter(
    (user) =>
      !nonBillableRoles.includes(user.roleId) &&
      user.accountType == "unlimited",
  );
  const billingIncrementText = getBillingIncrementText(
    newUsersEmailArray,
    existingUsersEmailArray,
  );

  const handleCreateTeam = async () => {
    if (!teamName.trim()) {
      setError("Please enter a team name to proceed.");
      return;
    }
    try {
      const { data } = await createTeam({
        variables: {
          teamName,
          location: teamLocation,
          createdAt: new Date().toISOString(),
        },
      });
      setNewTeam({
        uuid: data?.createTeam?.teamUuid || "",
        name: teamName,
      });
    } catch (err) {
      console.error("Create team error:", err);
      setError("An error occurred while creating the team.");
    }
  };

  const handleAddUsers = async () => {
    if (newUserEmails.length > 0) {
      try {
        const { data } = await getExistingUsers({
          variables: { emails: newUserEmails },
        });
        const {
          newUsersState: mappedNew,
          existingUsersState: mappedExisting,
          deactivatedUsers,
        } = mapExistingUsersResponse(data?.existingUsers);
        setNewUsersState(mappedNew || []);
        setExistingUsersState(mappedExisting || []);
        setDeactivatedUsersState(deactivatedUsers || []);
      } catch (err) {
        console.error("Get existing users error:", err);
        setError("An error occurred while fetching existing users.");
      }
    }
  };

  const handleConfirmNewUsers = async () => {
    const { ok, error: validationError } = validateUsers({
      newUsers: newUsersState,
      existingUsers: existingUsersState,
      isOrg: false,
    });
    if (!ok) {
      setError(validationError);
      return;
    }

    const usersInput = buildUsersInput({
      newUsers: newUsersState,
      existingUsers: existingUsersState,
      teamUuid: newTeam.uuid,
      createdAt: moment().format(),
      isOrg: false,
    });

    try {
      const result = await inviteUsersToTeam({
        variables: { inviteUsersToTeamInput: usersInput },
      });
      setReceiptData(
        result?.data?.inviteUsersToTeam?.provisionedUsers || {},
      );
    } catch (err) {
      console.error("Invite users error:", err);
      setError("An error occurred while inviting users.");
    }
  };
  const { refetchTemplates } = useTemplates();

  const [themeMode, setThemeMode] = useThemeMode();
  const originalThemeModeRef = useRef(themeMode);

  // Force dark mode during onboarding to prevent inconsistencies or light-mode flashing
  useEffect(() => {
    const originalThemeMode = originalThemeModeRef.current;
    setThemeMode("dark");

    return () => {
      setThemeMode(originalThemeMode);
    };
  }, [setThemeMode]);

  const { handleNext, handleBack: handleBackFromHook } =
    useOnboardingActions({
      step: currentStep,
      navigate,
      goToStep: navigateToStep,
      selectedRole: effectiveSelectedRole,
      allowedRoles,
      templateType,
      getValues: methods.getValues,
      handleCreateTeam,
      handleAddUsers,
      handleConfirmNewUsers,
      navigateTeamFlow,
      navigateFromRole,
      invitedTeamName,
      newUsersState,
      existingUsersState,
      referralSource,
      clinicGroup,
      refetchTemplates,
    });

  const handleBack = () => {
    handleBackFromHook({
      goBack,
      setIsTemplateLoading: () => {}, // Placeholder
      currentStep,
    });
  };

  useEffect(() => {
    const match = location.pathname.match(/\/onboarding\/?([^/]+)?/);
    const pathStep = match && match[1] ? match[1] : "welcome";
    const knownSteps = new Set([
      "welcome",
      "role",
      "template-choice",
      "template-setup",
      "cod-setup",
      "referral-source",
      "group-name",
      "team-setup-choice",
      "team-create",
      "team-add-users",
      "team-confirm",
      "team-receipt",
      "finish",
    ]);
    const isResetToWelcome =
      pathStep === "welcome" && currentStep !== "welcome";
    if (
      knownSteps.has(pathStep) &&
      pathStep !== currentStep &&
      !isResetToWelcome
    ) {
      if (location.pathname !== `/onboarding/${pathStep}`) {
        navigate(`/onboarding/${pathStep}`, { replace: true });
      }
      syncToStep(pathStep);
    }
  }, [location.pathname, currentStep, syncToStep, navigate]);

  // Update URL params when state changes
  const updateSelectedRole = (role) => {
    setSelectedRole(role);
    setUrlParams({ selectedRole: role });
  };

  const updateReferralSource = (source) => {
    setReferralSource(source);
    setUrlParams({ referralSource: source });
  };

  const updateClinicGroup = (group) => {
    setClinicGroup(group);
    setUrlParams({ clinicGroup: group });
  };

  const updateTemplateType = (type) => {
    setTemplateType(type);
    setUrlParams({ templateType: type });
  };

  const stepConfigs = {
    welcome: {
      title: "Welcome",
      description: "Get started with Scribenote",
      hideNav: true,
      hideProgress: true,
    },
    role: {
      title: "Role Selection",
      description: "Choose your role",
      hideNav: false,
      hideProgress: false,
    },
    "template-choice": {
      title: "Template Choice",
      description: "Select your template preference",
      hideNav: false,
      hideProgress: false,
    },
    "template-setup": {
      title: "Template Setup",
      description: "Configure your template fields",
      hideNav: false,
      hideProgress: false,
    },
    "cod-setup": {
      title: "Custom Objective Defaults",
      description: "Choose your default objective fields",
      hideNav: false,
      hideProgress: false,
    },
    "referral-source": {
      title: "Referral Source",
      description: "How did you hear about us?",
      hideNav: false,
      hideProgress: false,
    },
    "group-name": {
      title: "Group Affiliation",
      description: "Tell us about your clinic",
      hideNav: false,
      hideProgress: false,
    },

    "team-setup-choice": {
      title: "Team Setup",
      description: "Choose whether to create a team now or later",
      hideNav: false,
      hideProgress: false,
    },

    "team-create": {
      title: "Create Team",
      description: "Create your team",
      hideNav: false,
      hideProgress: false,
    },
    "team-add-users": {
      title: "Invite Teammates",
      description: "Add team members",
      hideNav: false,
      hideProgress: false,
    },
    "team-confirm": {
      title: "Confirm Invitations",
      description: "Review invites",
      hideNav: false,
      hideProgress: false,
    },
    "team-receipt": {
      title: "Team Members Invited",
      description: "Summary of your team invitations",
      hideNav: false,
      hideProgress: false,
    },
    finish: {
      title: "All Set",
      description: "Wrap up onboarding",
      hideNav: false,
      hideProgress: false,
    },
  };

  const stepConfig = stepConfigs[currentStep] || stepConfigs.welcome;

  // Render step component
  const renderStepComponent = () => {
    switch (currentStep) {
      case "welcome":
        return (
          <WelcomeStep
            onRemindMeLater={() => {
              skipOnboarding();
              navigate("/dashboard");
              mixpanel.track("Onboarding: Step Navigation", {
                from: "welcome",
                to: "finish",
                action: "remind_me_later",
              });
            }}
            onGetStarted={() => handleNext()}
            invitedTeamName={invitedTeamName}
          />
        );
      case "role":
        return (
          <RoleStep
            selectedRole={effectiveSelectedRole}
            setSelectedRole={updateSelectedRole}
          />
        );
      case "template-choice":
        return (
          <TemplateChoiceStep
            selected={templateType}
            setSelected={updateTemplateType}
          />
        );
      case "template-setup":
        return (
          <TemplateSetupStep
            templateType={templateType}
            methods={methods}
            onTemplateLoaded={setTemplateLoaded}
          />
        );
      case "team-setup-choice":
        return (
          <TeamSetupChoiceStep
            onCreateTeam={() => navigateToStep("team-create")}
          />
        );
      case "cod-setup":
        return <CodSetupStep formMethods={methods} />;
      case "referral-source":
        return (
          <ReferralSourceStep
            referralSource={referralSource}
            setReferralSource={updateReferralSource}
          />
        );
      case "group-name":
        return (
          <GroupNameStep
            clinicGroup={clinicGroup}
            setClinicGroup={updateClinicGroup}
          />
        );

      case "team-create":
        return (
          <TeamCreateStep
            onBack={handleBack}
            onNext={handleNext}
            teamName={teamName}
            setTeamName={setTeamName}
            setTeamLocation={setTeamLocation}
            error={error}
            onSkip={() => navigateToStep("referral-source")}
          />
        );
      case "team-add-users":
        return (
          <TeamAddUsersStep
            newUserEmails={newUserEmails}
            setNewUserEmails={setNewUserEmails}
            team={newTeam}
          />
        );
      case "team-confirm":
        return (
          <TeamConfirmStep
            onBack={handleBack}
            onNext={handleNext}
            newUsers={newUsersState}
            existingUsers={existingUsersState}
            deactivatedUsers={deactivatedUsersState}
            setUsersState={({ newUsers, existingUsers }) => {
              setNewUsersState(newUsers);
              setExistingUsersState(existingUsers);
              setNewUserEmails(
                [...newUsers, ...existingUsers].map(
                  (user) => user.email,
                ),
              );
            }}
            hasBillingSetup={hasBillingSetup}
            orgIsManualBilling={orgIsManualBilling}
            error={error}
            billingIncrementText={billingIncrementText}
          />
        );
      case "team-receipt":
        return <TeamReceiptStep receiptData={receiptData} />;
      case "finish":
        return <FinishStep />;
      default:
        return <div>Step not found</div>;
    }
  };

  const STEPS_WITH_BACK_DISABLED = [
    "welcome",
    "team-add-users",
    "team-confirm",
    "team-receipt",
  ];

  return (
    <div
      className="fixed inset-0 w-screen h-screen bg-white dark:bg-gray-900 z-50 flex flex-col"
      style={{
        backgroundImage: `url(${starrySky})`,
        backgroundRepeat: "repeat-x",
        backgroundPosition: "top",
        backgroundSize: "auto",
      }}
    >
      {!stepConfig.hideProgress && !invitedTeamName && (
        <ProgressBar progress={progress} />
      )}

      {error && (
        <div className="bg-danger-subtle border border-danger text-danger px-4 py-3 mx-4 mt-4 rounded-sm">
          <span className="block sm:inline">{error}</span>
          <button
            onClick={() => setError("")}
            className="float-right font-bold text-danger"
          >
            ×
          </button>
        </div>
      )}

      <div
        className={`h-full flex flex-col items-center ${
          stepConfig.hideNav ? "pt-24" : "pb-20 pt-24"
        }`}
      >
        <div className="mb-8 flex-1 overflow-y-auto w-full h-full max-w-[97vw] px-4">
          {renderStepComponent()}
        </div>

        {!stepConfig.hideNav && (
          <OnboardingNavButtons
            hideBack={STEPS_WITH_BACK_DISABLED.includes(currentStep)}
            hideNext={false}
            onBack={handleBack}
            onNext={handleNext}
            disableBack={currentStep === "welcome"}
            disableNext={
              (currentStep === "role" && !effectiveSelectedRole) ||
              (currentStep === "template-choice" && !templateType) ||
              (currentStep === "template-setup" && !templateLoaded) ||
              (currentStep === "team-create" && !teamName?.trim())
            }
            nextText={
              currentStep === "finish"
                ? "Finish"
                : currentStep === "team-setup-choice"
                  ? "Skip"
                  : "Next"
            }
          />
        )}
      </div>
    </div>
  );
}
